import { useState, lazy, Suspense } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Rocket,
  Search,
  Megaphone,
  ShoppingBag,
  Boxes,
  ShieldCheck,
  CheckCircle2,
  ArrowUpRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SectionHeading, Reveal } from "@/components/Reveal";
import { scrollToId } from "@/components/Navbar";
import {
  SeoRankVisual,
  FunnelVisual,
  CommerceBarsVisual,
  StockDashboardVisual,
  ShieldVisual,
  TimelineWeekVisual,
} from "@/components/visuals/AnimatedVisuals";

const Bars3D = lazy(() => import("@/components/visuals/Bars3D"));

const Commerce3DVisual = () => (
  <div className="relative w-full h-full min-h-[280px] rounded-2xl bg-[#0d0e14] border border-border overflow-hidden flex flex-col" data-testid="visual-commerce-3d">
    <div className="flex items-center justify-between px-5 pt-5">
      <span className="text-xs uppercase tracking-widest text-muted-foreground">
        Revenue Growth · 6 Months · 3D
      </span>
      <span className="text-xs font-semibold bg-primary/15 text-primary border border-primary/30 rounded-full px-3 py-1">
        +64%
      </span>
    </div>
    <div className="flex-1 min-h-[240px]">
      <Suspense fallback={<Skeleton className="w-full h-full bg-secondary/40" />}>
        <Bars3D />
      </Suspense>
    </div>
    <div className="flex justify-between px-6 pb-4 text-[10px] font-mono-meta text-muted-foreground">
      <span>Month 1</span>
      <span>Month 3</span>
      <span className="text-primary">Month 6</span>
    </div>
  </div>
);

const SYSTEMS = [
  {
    id: "website",
    icon: Rocket,
    label: "Website in 7 Days",
    headline: "From brief to live website in one week — maximum",
    bullets: [
      "Day 1–2: design created and approved by you",
      "Day 3–5: build, features, content and database",
      "Day 6: full testing on every device",
      "Day 7: live to the public — domain, hosting & SSL included",
    ],
    visual: TimelineWeekVisual,
  },
  {
    id: "seo",
    icon: Search,
    label: "Google SEO",
    headline: "Watch your business climb to page one",
    bullets: [
      "Full technical audit of your website",
      "Keyword strategy targeting buyers, not browsers",
      "On-page optimisation and authority building",
      "Monthly ranking reports you can actually read",
    ],
    visual: SeoRankVisual,
  },
  {
    id: "marketing",
    icon: Megaphone,
    label: "Digital Marketing",
    headline: "Every pound tracked from ad to sale",
    bullets: [
      "Campaigns across Google and Meta platforms",
      "Landing pages engineered to convert",
      "Full-funnel tracking — impressions to revenue",
      "Weekly optimisation, monthly clear reporting",
    ],
    visual: FunnelVisual,
  },
  {
    id: "ecommerce",
    icon: ShoppingBag,
    label: "E-Commerce Growth",
    headline: "Four platforms, one growth engine",
    bullets: [
      "Amazon FBA, Private Label and White Label",
      "Shopify stores with any feature integrated",
      "eBay and Etsy dropshipping operations",
      "Inventory and orders connected across channels",
    ],
    visual: Commerce3DVisual,
  },
  {
    id: "systems",
    icon: Boxes,
    label: "Custom Business Systems",
    headline: "Describe your business — we build the system",
    bullets: [
      "Warehouse stock counting with barcode scanning",
      "Camera and monitoring systems",
      "Automated reporting dashboards",
      "Built around exactly how your business works",
    ],
    visual: StockDashboardVisual,
  },
  {
    id: "security",
    icon: ShieldCheck,
    label: "Cyber Security",
    headline: "Enterprise-grade protection, SME budget",
    bullets: [
      "Security audits and server hardening",
      "24/7 monitoring and threat blocking",
      "Company-wide 2FA and access control",
      "Team training so the human layer holds too",
    ],
    visual: ShieldVisual,
  },
];

export const SystemsShowcase = () => {
  const [active, setActive] = useState(SYSTEMS[0]);
  const ActiveVisual = active.visual;

  return (
    <section id="systems" className="py-16 sm:py-20 lg:py-24" data-testid="systems-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Interactive · How It Works"
          title="See your system before it's built"
          description="Pick a service and watch exactly how it works — live animated visualisations of the systems we deliver."
        />

        <Reveal className="mt-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-3 flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0" data-testid="systems-tabs">
              {SYSTEMS.map((system) => {
                const Icon = system.icon;
                const isActive = active.id === system.id;
                return (
                  <button
                    key={system.id}
                    onClick={() => setActive(system)}
                    className={`shrink-0 lg:w-full flex items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm transition-colors duration-200 ${
                      isActive
                        ? "bg-primary/10 border-primary/40 text-foreground"
                        : "bg-card border-border text-muted-foreground hover:text-foreground hover:border-primary/25"
                    }`}
                    data-testid={`systems-tab-${system.id}`}
                  >
                    <Icon className={`w-4 h-4 shrink-0 ${isActive ? "text-primary" : ""}`} />
                    <span className="whitespace-nowrap lg:whitespace-normal">{system.label}</span>
                    {isActive && (
                      <motion.span
                        layoutId="system-active-dot"
                        className="gold-dot ml-auto hidden lg:block"
                      />
                    )}
                  </button>
                );
              })}
            </div>

            <div className="lg:col-span-9">
              <AnimatePresence mode="wait">
                <motion.div
                  key={active.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch"
                  data-testid="systems-active-panel"
                >
                  <div className="card-highlight rounded-2xl bg-card border border-border p-6 sm:p-7 flex flex-col">
                    <h3 className="text-xl sm:text-2xl font-semibold tracking-tight text-foreground">
                      {active.headline}
                    </h3>
                    <ul className="mt-6 space-y-4 flex-1">
                      {active.bullets.map((bullet, i) => (
                        <motion.li
                          key={bullet}
                          initial={{ opacity: 0, x: -12 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.15 + i * 0.1, duration: 0.4 }}
                          className="flex items-start gap-3 text-sm text-muted-foreground leading-relaxed"
                        >
                          <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                          {bullet}
                        </motion.li>
                      ))}
                    </ul>
                    <Button
                      onClick={() => scrollToId("contact")}
                      variant="outline"
                      className="mt-7 w-fit rounded-xl border-primary/40 text-primary hover:bg-primary/10 hover:text-primary transition-colors duration-200"
                      data-testid="systems-contact-cta-button"
                    >
                      Get This For My Business <ArrowUpRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>

                  <div className="min-h-[300px]">
                    <ActiveVisual />
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
